/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled, { css } from "styled-components";

const StyledButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  --gap-space: ${(props) => {
    switch (props.$gap) {
      case "small":
        return "4px";
      case "medium":
        return "8px";
      case "large":
        return "16px";
      default:
        return "0px";
    }
  }};
  ${(props) => displayStyles[props.$displayAs] || css`
    align-items: center;
  `}
  ${(props) => justifyStyles[props.$justify]}
  ${(props) => gapStyles[props.$gap]}
  ${(props) => itemsWidthStyles[props.$itemsWidth]}

  > * {
    min-width: fit-content;
  }
`;

const gapStyles = {
  small: css`
    gap: 4px;
  `,
  medium: css`
    gap: 8px;
  `,
  large: css`
    gap: 16px;
  `,
  none: css`
    gap: 0;
  `,
};

const displayStyles = {
  inline: css`
    flex-direction: row;
  `,
  column: css`
    flex-direction: column;
  `,
};

const justifyStyles = {
  start: css`
    justify-content: flex-start;
  `,
  end: css`
    justify-content: flex-end;
  `,
  center: css`
    justify-content: center;
    text-align: center;

    ${({ $displayAs }) =>
      $displayAs === "column" &&
      css`
        align-items: center;
      `}
  `,
};

const itemsWidthStyles = {
  full: css`
    width: 100%;

    > * {
      width: 100%;
      flex: 1;
    }
  `,
  half: css`
    width: 100%;

    > * {
      flex: 0 0 calc(50% - var(--gap-space, 0px) / 2);

      ${({ theme }) => theme.containerMedia.sm} {
        flex: 1 0 auto;
      }
    }
  `,
  auto: css`
    width: auto;
  `,
  fillEmpty: css`
    width: 100%;

    > * {
      flex: 1 0 auto;
    }
  `,
};

const ButtonGroup = ({
  gap = "medium",
  displayAs = "inline",
  justify = "center",
  children,
  itemsWidth = "auto",
  ...props
}) => {
  return (
    <StyledButtonGroup $gap={gap} $displayAs={displayAs} $itemsWidth={itemsWidth} $justify={justify} {...props}>
      {children}
    </StyledButtonGroup>
  );
};

export default ButtonGroup;
